// getuserlist

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getProtestAndExamptions, updateProtestAndExemptionStatus, updateAssessedValues, updateContingencySplits, generatePropRefundInvoice, sendMarketValueEmail, sendSettlementEmail, uploadSupportingDocumentsApi, uploadSettlementWaiverDocumentApi } from "../../Api/AdminApiCall";

export const fetchprotestandexemptionsApi = createAsyncThunk(
    "admin/fetchprotestandexemptionsApi",
    async ({ page = 1, type, protest_status = '', search = '' }, { rejectWithValue }) => {
        try {
            const res = await getProtestAndExamptions(page, type, protest_status, search);
            return res.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || "Failed to load users"
            );
        }
    }
);

export const updateProtestAndExemptionStatusApi = createAsyncThunk(
    "admin/updateProtestAndExemptionStatusApi",
    async (data, { rejectWithValue }) => {
        try {
            const res = await updateProtestAndExemptionStatus(data);
            return res.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || "Failed to update protest and exemption status"
            );
        }
    }
);

export const uploadSupportingDocuments = createAsyncThunk(
    "admin/uploadSupportingDocuments",
    async ({ property_id, document_type, files, file_description }, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append("property_id", property_id);
            formData.append("document_type", document_type);
            if (file_description) {
                formData.append("file_description", file_description);
            }
            files.forEach((file) => formData.append("files", file));

            const res = await uploadSupportingDocumentsApi(formData);
            return res.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || "Failed to upload supporting documents"
            );
        }
    }
);

export const updateAssessedValuesApi = createAsyncThunk(
    "admin/updateAssessedValuesApi",
    async (data, { rejectWithValue }) => {
        try {
            const res = await updateAssessedValues(data);
            return res.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || "Failed to update assessed values"
            );
        }
    }
);

export const updateContingencySplitsApi = createAsyncThunk(
    "admin/updateContingencySplitsApi",
    async (data, { rejectWithValue }) => {
        try {
            const res = await updateContingencySplits(data);
            return res.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || "Failed to update contingency splits"
            );
        }
    }
);

export const uploadSettlementWaiverDocument = createAsyncThunk(
    "admin/uploadSettlementWaiverDocument",
    async ({ property_id, user_id, settlement_waiver_file, status = 'active' }, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append('property_id', property_id);
            formData.append('user_id', user_id);
            formData.append('settlement_waiver_file', settlement_waiver_file);
            formData.append('status', status);

            const res = await uploadSettlementWaiverDocumentApi(formData);
            return res.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || "Failed to upload settlement waiver document"
            );
        }
    }
);

export const generatePropRefundInvoiceApi = createAsyncThunk(
    "admin/generatePropRefundInvoiceApi",
    async (data, { rejectWithValue }) => {
        try {
            const res = await generatePropRefundInvoice(data);
            return res.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || "Failed to generate refund invoice"
            );
        }
    }
);

export const sendMarketValueEmailApi = createAsyncThunk(
    "admin/sendMarketValueEmailApi",
    async (data, { rejectWithValue }) => {
        try {
            const res = await sendMarketValueEmail(data);
            return res.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || "Failed to send market value email"
            );
        }
    }
);

export const sendSettlementEmailApi = createAsyncThunk(
    "admin/sendSettlementEmailApi",
    async (data, { rejectWithValue }) => {
        try {
            const res = await sendSettlementEmail(data);
            return res.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || "Failed to send settlement email"
            );
        }
    }
);


const ProtestExamptionSlice = createSlice({
    name: "protestAndExamptions",
    initialState: {
        ProtestAndExamptions: [],
        ProtestUpdate: [],
        supportingUpload: null,
        supportingUploadLoading: false,
        settlementWaiverUpload: null,
        settlementWaiverUploadLoading: false,
        assessedValuesUpdateLoading: false,
        contingencySplitsUpdateLoading: false,
        generateInvoiceLoading: false,
        sendMarketValueEmailLoading: false,
        loading: false,
        error: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchprotestandexemptionsApi.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchprotestandexemptionsApi.fulfilled, (state, action) => {
                state.loading = false;
                state.ProtestAndExamptions = action.payload;
            })
            .addCase(fetchprotestandexemptionsApi.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(updateProtestAndExemptionStatusApi.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateProtestAndExemptionStatusApi.fulfilled, (state, action) => {
                state.loading = false;
                state.ProtestUpdate = action.payload;
            })
            .addCase(updateProtestAndExemptionStatusApi.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(uploadSupportingDocuments.pending, (state) => {
                state.supportingUploadLoading = true;
                state.error = null;
            })
            .addCase(uploadSupportingDocuments.fulfilled, (state, action) => {
                state.supportingUploadLoading = false;
                state.supportingUpload = action.payload;
            })
            .addCase(uploadSupportingDocuments.rejected, (state, action) => {
                state.supportingUploadLoading = false;
                state.error = action.payload;
            })
            .addCase(uploadSettlementWaiverDocument.pending, (state) => {
                state.settlementWaiverUploadLoading = true;
                state.error = null;
            })
            .addCase(uploadSettlementWaiverDocument.fulfilled, (state, action) => {
                state.settlementWaiverUploadLoading = false;
                state.settlementWaiverUpload = action.payload;
            })
            .addCase(uploadSettlementWaiverDocument.rejected, (state, action) => {
                state.settlementWaiverUploadLoading = false;
                state.error = action.payload;
            })
            .addCase(updateAssessedValuesApi.pending, (state) => {
                state.assessedValuesUpdateLoading = true;
                state.error = null;
            })
            .addCase(updateAssessedValuesApi.fulfilled, (state) => {
                state.assessedValuesUpdateLoading = false;
            })
            .addCase(updateAssessedValuesApi.rejected, (state, action) => {
                state.assessedValuesUpdateLoading = false;
                state.error = action.payload;
            })
            .addCase(updateContingencySplitsApi.pending, (state) => {
                state.contingencySplitsUpdateLoading = true;
                state.error = null;
            })
            .addCase(updateContingencySplitsApi.fulfilled, (state) => {
                state.contingencySplitsUpdateLoading = false;
            })
            .addCase(updateContingencySplitsApi.rejected, (state, action) => {
                state.contingencySplitsUpdateLoading = false;
                state.error = action.payload;
            })
            .addCase(generatePropRefundInvoiceApi.pending, (state) => {
                state.generateInvoiceLoading = true;
                state.error = null;
            })
            .addCase(generatePropRefundInvoiceApi.fulfilled, (state) => {
                state.generateInvoiceLoading = false;
            })
            .addCase(generatePropRefundInvoiceApi.rejected, (state, action) => {
                state.generateInvoiceLoading = false;
                state.error = action.payload;
            })
            .addCase(sendMarketValueEmailApi.pending, (state) => {
                state.sendMarketValueEmailLoading = true;
                state.error = null;
            })
            .addCase(sendMarketValueEmailApi.fulfilled, (state) => {
                state.sendMarketValueEmailLoading = false;
            })
            .addCase(sendMarketValueEmailApi.rejected, (state, action) => {
                state.sendMarketValueEmailLoading = false;
                state.error = action.payload;
            })
            .addCase(sendSettlementEmailApi.pending, (state) => {
                state.sendSettlementEmailLoading = true;
                state.error = null;
            })
            .addCase(sendSettlementEmailApi.fulfilled, (state) => {
                state.sendSettlementEmailLoading = false;
            })
            .addCase(sendSettlementEmailApi.rejected, (state, action) => {
                state.sendSettlementEmailLoading = false;
                state.error = action.payload;
            })
    },
});

const ProtestExamptionsliceReducer = ProtestExamptionSlice.reducer;

export default ProtestExamptionsliceReducer;
